import { useRef, useState, useEffect, useCallback, TouchEvent } from 'react';

interface UseHoldToRepeatOptions {
  /** Minimum allowed value */
  min: number;
  /** Maximum allowed value */
  max: number;
  /** Initial delay before repeating starts (ms) */
  initialDelay?: number;
  /** Initial interval between repeats (ms) */
  initialInterval?: number;
  /** Time after which to accelerate (ms) */
  accelerateAfter?: number;
  /** Faster interval after acceleration (ms) */
  acceleratedInterval?: number;
  /** Optional callback fired on every local value change (for syncing UI elements like sliders) */
  onLocalChange?: (value: number) => void;
}

interface ButtonHandlers {
  onMouseDown: () => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: (e: TouchEvent) => void;
  onTouchEnd: (e: TouchEvent) => void;
  onTouchCancel: (e: TouchEvent) => void;
}

interface UseHoldToRepeatReturn {
  /** Current local value (use this for UI display) */
  localValue: number;
  /** Set local value directly (for external sync like slider drag) */
  setLocalValue: (value: number) => void;
  /** Event handlers for increment button */
  incrementHandlers: ButtonHandlers;
  /** Event handlers for decrement button */
  decrementHandlers: ButtonHandlers;
}

/**
 * Custom hook for press-and-hold increment/decrement behavior with batched updates.
 *
 * Updates local state rapidly during hold for responsive UI, but only calls
 * onCommit with the final value when the hold is released.
 *
 * @param externalValue - The current value from external state (e.g., Redux)
 * @param onCommit - Callback to persist the final value (called once on release)
 * @param options - Configuration options
 */
export function useHoldToRepeat(
  externalValue: number,
  onCommit: (value: number) => void,
  options: UseHoldToRepeatOptions,
): UseHoldToRepeatReturn {
  const {
    min,
    max,
    initialDelay = 200,
    initialInterval = 100,
    accelerateAfter = 1000,
    acceleratedInterval = 40,
    onLocalChange,
  } = options;

  // Local state for responsive UI during hold
  const [localValue, setLocalValue] = useState(externalValue);

  // Refs for timer management
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const holdIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const holdStartTimeRef = useRef<number>(0);
  const localValueRef = useRef(localValue);
  const isHoldingRef = useRef(false);

  // Keep localValueRef in sync with localValue
  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  // Sync local state when external value changes (but not during a hold)
  useEffect(() => {
    if (!isHoldingRef.current) {
      setLocalValue(externalValue);
    }
  }, [externalValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
    };
  }, []);

  const increment = useCallback(() => {
    setLocalValue((prev) => {
      const newValue = Math.min(prev + 1, max);
      localValueRef.current = newValue;
      onLocalChange?.(newValue);
      return newValue;
    });
  }, [max, onLocalChange]);

  const decrement = useCallback(() => {
    setLocalValue((prev) => {
      const newValue = Math.max(prev - 1, min);
      localValueRef.current = newValue;
      onLocalChange?.(newValue);
      return newValue;
    });
  }, [min, onLocalChange]);

  const stopHold = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }

    // Commit the final value to external state only if we were holding
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      onCommit(localValueRef.current);
    }
  }, [onCommit]);

  const startHold = useCallback(
    (action: () => void) => {
      // Clear any existing timers
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);

      isHoldingRef.current = true;
      holdStartTimeRef.current = Date.now();

      // Execute once immediately
      action();

      // After initial delay, start repeating
      holdTimeoutRef.current = setTimeout(() => {
        let intervalMs = initialInterval;

        const runInterval = () => {
          holdIntervalRef.current = setInterval(() => {
            action();

            // Accelerate after holding for a while
            const elapsed = Date.now() - holdStartTimeRef.current;
            if (elapsed > accelerateAfter && intervalMs > acceleratedInterval) {
              clearInterval(holdIntervalRef.current!);
              intervalMs = acceleratedInterval;
              runInterval();
            }
          }, intervalMs);
        };

        runInterval();
      }, initialDelay);
    },
    [initialDelay, initialInterval, accelerateAfter, acceleratedInterval],
  );

  const handleIncMouseDown = useCallback(() => startHold(increment), [startHold, increment]);
  const handleDecMouseDown = useCallback(() => startHold(decrement), [startHold, decrement]);

  // Touch handlers call preventDefault() to suppress synthetic mouse events
  // Without this, a single tap causes double increment (touchstart + mousedown ~300ms later)
  const handleIncTouchStart = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      startHold(increment);
    },
    [startHold, increment],
  );

  const handleDecTouchStart = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      startHold(decrement);
    },
    [startHold, decrement],
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      stopHold();
    },
    [stopHold],
  );

  // touchcancel fires when touch is interrupted (system gesture, incoming call, palm rejection)
  const handleTouchCancel = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      stopHold();
    },
    [stopHold],
  );

  return {
    localValue,
    setLocalValue,
    incrementHandlers: {
      onMouseDown: handleIncMouseDown,
      onMouseUp: stopHold,
      onMouseLeave: stopHold,
      onTouchStart: handleIncTouchStart,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel,
    },
    decrementHandlers: {
      onMouseDown: handleDecMouseDown,
      onMouseUp: stopHold,
      onMouseLeave: stopHold,
      onTouchStart: handleDecTouchStart,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel,
    },
  };
}
